'use client';

import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { AUTH_CHANGE_EVENT } from '@/lib/authEvents';

interface PermissionContextType {
    hasAdminPermission: boolean;
    isLoading: boolean;
}

const PermissionContext = createContext<PermissionContextType>({
    hasAdminPermission: false,
    isLoading: true,
});

export function usePermission() {
    return useContext(PermissionContext);
}

interface PermissionProviderProps {
    children: ReactNode;
}

function readUserId(): string | null {
    if (typeof window === 'undefined') return null;
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    try {
        return JSON.parse(userStr)?.id ?? null;
    } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
    }
}

export function PermissionProvider({ children }: PermissionProviderProps) {
    const [userId, setUserId] = useState<string | null>(null);
    const [isClient, setIsClient] = useState(false);
    const pathname = usePathname();

    const syncUserId = useCallback(() => {
        setUserId(prev => {
            const next = readUserId();
            return prev === next ? prev : next;
        });
    }, []);

    // Read the logged-in user from localStorage on the client, and re-read it
    // whenever auth changes (login / logout) so the permission check runs
    // without needing a full page refresh.
    useEffect(() => {
        setIsClient(true);
        syncUserId();

        window.addEventListener(AUTH_CHANGE_EVENT, syncUserId);
        window.addEventListener('storage', syncUserId);
        return () => {
            window.removeEventListener(AUTH_CHANGE_EVENT, syncUserId);
            window.removeEventListener('storage', syncUserId);
        };
    }, [syncUserId]);

    // Navigating (e.g. login -> home, logout -> login) also re-checks auth.
    useEffect(() => {
        if (isClient) syncUserId();
    }, [pathname, isClient, syncUserId]);

    const { data, isLoading } = useQuery({
        queryKey: ['permission', 'check-permission', userId],
        queryFn: async () => {

            const response = await api.post('/api/v1/tapanel/check-permission', {
                user_id: userId,
            });
            return response;
        },
        enabled: isClient && !!userId,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    const hasAdminPermission = data?.success === true;

    return (
        <PermissionContext.Provider
            value={{
                hasAdminPermission,
                isLoading: !isClient || (isLoading && !!userId),
            }}
        >   
            {children}
        </PermissionContext.Provider>
    );
}
